import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import { IconX, IconFileText } from "@tabler/icons-react";
import styles from "./header.module.css";
import useApi from "../../hooks/useApi";
import {
  GET_ALL_NOTIFICATIONS,
  MARK_NOTIFICATION_AS_READ,
} from "../../utils/apiConstants";

export default function Notifications({
  handleNotificationClose,
  unreadCount,
  setUnreadCount,
}) {
  const navigate = useNavigate();
  const { apiRequest } = useApi();

  const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Unread, 2: Read
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const fetchNotifications = useCallback(
    async (pageNum = 1, isInitial = false) => {
      if (loadingRef.current || (!hasMoreRef.current && !isInitial)) {
        return;
      }
      try {
        loadingRef.current = true;
        setLoadingNotifications(true);

        const filterMap = ["all", "unread", "read"];
        const response = await apiRequest({
          url: GET_ALL_NOTIFICATIONS,
          method: "get",
          params: {
            page: pageNum,
            limit: 10,
            filter: filterMap[activeTab],
          },
        });

        if (response?.data) {
          const {
            notifications: newNotifs,
            unreadCount: count,
            hasMore: more,
          } = response.data;

          setNotifications((prev) =>
            isInitial ? newNotifs : [...prev, ...newNotifs]
          );

          setUnreadCount(count);
          hasMoreRef.current = more;
          pageRef.current = pageNum;
        }
      } catch (error) {
        console.error("fetchNotifications error:", error);
      } finally {
        loadingRef.current = false;
        setLoadingNotifications(false);
      }
    },
    [activeTab, apiRequest, setUnreadCount]
  );

  useEffect(() => {
    fetchNotifications(1, true);
  }, [fetchNotifications]);

  const handleNotificationClick = useCallback(
    async (notification) => {
      try {
        if (!notification.isRead) {
          await apiRequest({
            url: MARK_NOTIFICATION_AS_READ,
            method: "post",
            data: {
              notificationId: notification.id,
            },
          });
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === notification.id ? { ...n, isRead: true } : n
            )
          );
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
        navigate(notification.redirectRoute);
        handleNotificationClose();
      } catch (error) {
        console.error("handleNotificationClick error:", error);
        navigate(notification.redirectRoute);
      }
    },
    [apiRequest, navigate, handleNotificationClose, setUnreadCount]
  );

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
      if (
        scrollHeight - scrollTop <= clientHeight + 50 &&
        hasMoreRef.current &&
        !loadingRef.current
      ) {
        fetchNotifications((pageRef.current || 1) + 1);
      }
    },
    [fetchNotifications]
  );

  const getTimeAgo = useCallback((dateString) => {
    const diff = new Date() - new Date(dateString);
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    if (hours < 24) return `${hours} hrs ago`;
    if (days < 7) return `${days} days ago`;
    return new Date(dateString).toLocaleDateString();
  }, []);

  return (
    <>
      <div className={styles.notificationHeader}>
        <Typography variant="h6" fontWeight="bold">
          Notifications
        </Typography>
        <IconButton size="small" onClick={handleNotificationClose}>
          <IconX size={20} />
        </IconButton>
      </div>
      <Box>
        <Tabs
          value={activeTab}
          onChange={(e, v) => {
            setActiveTab(v);
            pageRef.current = 1;
            hasMoreRef.current = true;
          }}
          variant="fullWidth"
          textColor="inherit"
          TabIndicatorProps={{ style: { backgroundColor: "#7f68ba" } }}
        >
          <Tab label="All" sx={{ textTransform: "none", fontWeight: 600 }} />
          <Tab label="Unread" sx={{ textTransform: "none", fontWeight: 600 }} />
          <Tab label="Read" sx={{ textTransform: "none", fontWeight: 600 }} />
        </Tabs>
      </Box>
      <div className={styles.notificationList} onScroll={handleScroll}>
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`${styles.notificationItem} ${!notification.isRead ? styles.unreadItem : ""
                }`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className={styles.notificationIcon}>
                <IconFileText size={24} color="#7f68ba" />
              </div>
              <div className={styles.notificationContent}>
                <div className={styles.notificationTitle}>
                  {notification.title}
                </div>
                <div className={styles.notificationMessage}>
                  {notification.message}
                </div>
              </div>
              <div className={styles.notificationMeta}>
                {!notification.isRead && <div className={styles.unreadDot} />}
                <div className={styles.notificationTime}>
                  {getTimeAgo(notification.createdAt)}
                </div>
              </div>
            </div>
          ))
        ) : !loadingNotifications ? (
          <div className="p-4 text-center">
            <Typography variant="body2" color="textSecondary">
              No notifications found.
            </Typography>
          </div>
        ) : null}
        {loadingNotifications && (
          <div className="text-center p-2">
            <CircularProgress size={20} />
          </div>
        )}
      </div>
    </>
  );
}
